/**
 * Passkey エラー変換ユーティリティ（`@auth/ui-react` 共有版）。
 *
 * passkey 操作で投げられる `@auth/client` の `AuthError`（`code` + `isRateLimited()` /
 * `isAccountLocked()`）とブラウザ WebAuthn 例外（`DOMException`）を、一元的に日本語
 * メッセージ + キャンセル判定へ変換する純粋関数群。全アプリの passkey ログインで同一の
 * 文言・挙動を担保するため、ダッシュボード実装から本共有ライブラリへ昇格した。
 *
 * 独自のエラーコード体系は導入せず、`@auth/client` の `AuthError` コード規約のみを解釈する。
 */
import { AuthError } from "@auth/client";
/** ラベル長制約（1〜64文字）の最小値。 */
const LABEL_MIN_LENGTH = 1;
/** ラベル長制約（1〜64文字）の最大値。 */
const LABEL_MAX_LENGTH = 64;
/** 認証失敗メッセージ（AUTH_001）。 */
const MESSAGE_AUTH_FAILED = "認証に失敗しました。もう一度お試しください。";
/** 有効期限切れメッセージ（AUTH_002）。 */
const MESSAGE_EXPIRED = "有効期限が切れました。もう一度お試しください。";
/** 未認証メッセージ（AUTH_004）。 */
const MESSAGE_UNAUTHENTICATED = "ログインが必要です。再度ログインしてください。";
/** レート制限メッセージ（429 / RATE_LIMIT_001）。 */
const MESSAGE_RATE_LIMITED = "リクエストが多すぎます。しばらくしてから再度お試しください。";
/** アカウントロックメッセージ（423 / AUTH_005）。 */
const MESSAGE_ACCOUNT_LOCKED = "アカウントがロックされています。時間をおいて再度お試しください。";
/** ラベル検証メッセージ（VALIDATION_001 / クライアント検証）。 */
const MESSAGE_LABEL_INVALID = "ラベルは1〜64文字で入力してください。";
/** その他/ネットワーク/不明エラーメッセージ。 */
const MESSAGE_NETWORK = "ネットワークエラーが発生しました。接続を確認してください。";
/** 非キャンセル結果を生成するヘルパ。 */
function message(text) {
    return { message: text, cancelled: false };
}
/**
 * WebAuthn のキャンセル/タイムアウト（`DOMException` の `NotAllowedError`）か判定する。
 */
function isWebAuthnCancellation(error) {
    return error instanceof DOMException && error.name === "NotAllowedError";
}
/**
 * `AuthError` をコード規約に基づき日本語メッセージへ変換する。
 * 規約外コードは `MESSAGE_NETWORK` へフォールバックする。
 */
function mapAuthError(error) {
    // HTTP 429（RATE_LIMIT_001）/ 423（AUTH_005）は専用判定メソッドを優先する。
    if (error.isRateLimited()) {
        return message(MESSAGE_RATE_LIMITED);
    }
    if (error.isAccountLocked()) {
        return message(MESSAGE_ACCOUNT_LOCKED);
    }
    switch (error.code) {
        case "AUTH_001":
            return message(MESSAGE_AUTH_FAILED);
        case "AUTH_002":
            return message(MESSAGE_EXPIRED);
        case "AUTH_004":
            return message(MESSAGE_UNAUTHENTICATED);
        case "VALIDATION_001":
            return message(MESSAGE_LABEL_INVALID);
        default:
            return message(MESSAGE_NETWORK);
    }
}
/**
 * passkey 操作のエラーを日本語メッセージ + キャンセル判定へ変換する。
 *
 * - WebAuthn `NotAllowedError` → `cancelled=true` / message 空（トースト非表示）。
 * - `AuthError` → コード規約（AUTH_001/002/004、429、423、VALIDATION_001）で変換。
 * - それ以外（ネットワーク / 不明）→ `MESSAGE_NETWORK`。
 */
export function toPasskeyErrorMessage(error) {
    if (isWebAuthnCancellation(error)) {
        return { message: "", cancelled: true };
    }
    if (error instanceof AuthError) {
        return mapAuthError(error);
    }
    return message(MESSAGE_NETWORK);
}
/**
 * passkey ラベルを 1〜64 文字で検証する純粋関数（前後空白は trim して判定）。
 *
 * @returns 妥当なら null、違反時は日本語エラーメッセージ
 */
export function validatePasskeyLabel(label) {
    const trimmedLength = label.trim().length;
    if (trimmedLength < LABEL_MIN_LENGTH || trimmedLength > LABEL_MAX_LENGTH) {
        return MESSAGE_LABEL_INVALID;
    }
    return null;
}
